import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";
import { PageHeader, NoFarm } from "@/components/Page";
import { Card } from "@/components/ui/card";
import {
  CloudSun,
  Droplets,
  Wind,
  Thermometer,
  Sprout,
  TrendingUp,
} from "lucide-react";

export default function Weather() {
  const { farmId, farm, loading } = useFarm();

  const { data, isError } = useQuery({
    queryKey: ["weather", farmId],
    queryFn: async () => (await api.get(`/farms/${farmId}/weather`)).data,
    enabled: !!farmId,
  });

  if (!loading && !farmId) return <NoFarm />;

  if (!data) {
    return (
      <div className="text-muted-foreground">
        Loading weather…
      </div>
    );
  }

  if (isError || data.error) {
    return (
      <div className="text-muted-foreground">
        Weather unavailable right now.
      </div>
    );
  }

  const c = data.current;
  const growth = data.grass_growth;
  const todayGrowth = growth?.today;
  const growthForecast = growth?.forecast ?? [];

  return (
    <div>
      <PageHeader
        title="Weather"
        subtitle={`Forecast for ${farm?.name} · grazing conditions, rainfall and grass growth`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card
          className="lg:col-span-1 p-6 bg-[hsl(148,38%,16%)] text-white"
          data-testid="weather-current"
        >
          <div className="flex items-center gap-2 text-white/80">
            <CloudSun className="h-5 w-5" />
            <span className="text-sm">{c.summary}</span>
          </div>

          <p className="font-display text-6xl font-extrabold mt-4 tabular-nums">
            {Math.round(c.temperature)}°
          </p>

          <div className="grid grid-cols-3 gap-2 mt-6 text-center">
            <div>
              <Droplets className="h-4 w-4 mx-auto mb-1 text-white/70" />
              <p className="text-xs text-white/70">Rain</p>
              <p className="font-semibold tabular-nums">
                {c.precipitation} mm
              </p>
            </div>

            <div>
              <Wind className="h-4 w-4 mx-auto mb-1 text-white/70" />
              <p className="text-xs text-white/70">Wind</p>
              <p className="font-semibold tabular-nums">
                {Math.round(c.wind_speed)} km/h
              </p>
            </div>

            <div>
              <Thermometer className="h-4 w-4 mx-auto mb-1 text-white/70" />
              <p className="text-xs text-white/70">Humidity</p>
              <p className="font-semibold tabular-nums">
                {c.humidity}%
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2 rounded-lg bg-white/10 p-3">
            <Sprout className="h-4 w-4 mt-0.5 shrink-0" />
            <p className="text-sm text-white/90">
              {data.grazing_note}
            </p>
          </div>
        </Card>

        <Card className="lg:col-span-2 p-6">
          <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)] mb-4">
            7-Day Weather Outlook
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {data.daily.map((d) => (
              <div
                key={d.date}
                className="rounded-lg border border-border p-4 text-center"
                data-testid={`weather-day-${d.date}`}
              >
                <p className="text-xs font-semibold text-muted-foreground">
                  {new Date(d.date).toLocaleDateString(undefined, {
                    weekday: "short",
                  })}
                </p>

                <CloudSun className="h-6 w-6 mx-auto my-2 text-primary" />

                <p className="text-xs text-muted-foreground mb-2 h-8">
                  {d.summary}
                </p>

                <p className="font-semibold tabular-nums text-[hsl(148,38%,16%)]">
                  {Math.round(d.temp_max)}° / {Math.round(d.temp_min)}°
                </p>

                <p className="text-xs text-blue-600 mt-1 tabular-nums">
                  {d.precipitation} mm
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card
          className="lg:col-span-1 p-6"
          data-testid="grass-growth-today"
        >
          <div className="flex items-center gap-2">
            <Sprout className="h-5 w-5 text-primary" />
            <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              Predicted Grass Growth
            </h3>
          </div>

          {todayGrowth ? (
            <>
              <div className="mt-5 flex items-end gap-2">
                <p className="font-display text-5xl font-extrabold tabular-nums text-[hsl(148,38%,16%)]">
                  {todayGrowth.predicted_growth}
                </p>

                <p className="text-sm text-muted-foreground mb-1">
                  kg DM/ha/day
                </p>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Today
                </span>

                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  {todayGrowth.classification}
                </span>
              </div>

              <div className="mt-4 rounded-lg bg-muted p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <p className="text-sm font-medium">
                    7-day average
                  </p>
                </div>

                <p className="mt-1 text-xl font-bold tabular-nums text-[hsl(148,38%,16%)]">
                  {growth.seven_day_average} kg DM/ha/day
                </p>
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Weather-based prototype prediction. Manual farm measurements
                should take priority where available.
              </p>
            </>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              Grass-growth prediction unavailable.
            </p>
          )}
        </Card>

        <Card className="lg:col-span-2 p-6">
          <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)] mb-4">
            7-Day Grass Growth Forecast
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {growthForecast.map((d) => (
              <div
                key={d.date}
                className="rounded-lg border border-border p-4 text-center"
                data-testid={`growth-day-${d.date}`}
              >
                <p className="text-xs font-semibold text-muted-foreground">
                  {new Date(d.date).toLocaleDateString(undefined, {
                    weekday: "short",
                  })}
                </p>

                <Sprout className="h-6 w-6 mx-auto my-2 text-primary" />

                <p className="font-display text-2xl font-bold tabular-nums text-[hsl(148,38%,16%)]">
                  {d.predicted_growth}
                </p>

                <p className="text-xs text-muted-foreground">
                  kg DM/ha/day
                </p>

                <p className="text-xs font-medium text-primary mt-2">
                  {d.classification}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}